import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import Pill from '@pelckmans/elementis/components/pill';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMedialinkById, Medialink } from '../../../../../api/medialink';
import { Node } from '../../../../../api/table-of-content';
import { getUserMaterialById } from '../../../../../api/user-material';
import ModalContext from '../../../../../contexts/ModalContext';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import LoadingState from '../../../../../shared-components/LoadingState';
import { HorizontalScrollContextProvider } from '../../../../../shared-components/table/context/HorizontalScrollContext';
import ModuleContext from '../../../_contexts/ModuleContext';
import { MedialinkDialogHeaderTagsWithoutPillGroup } from '../../../components/MedialinkDialogHeaderTags';
import { UserMaterialTags } from '../../../components/UserMaterialDialogHeaderTags';
import { getSessions } from '../../api/history';
import { ScorableItem } from '../../api/scorable-items';
import { useTableOfContent } from '../TOCDataContext';
import HistoryTable from './HistoryTable';

const BASE_PATH = 'PAGES.RESULTS.SESSION_HISTORY_MODAL';

export default function HistoryModal({
  scorableItem,
  memberId,
  groupId,
  moduleId,
  isAssignment,
  id,
}: {
  scorableItem: ScorableItem;
  memberId: string;
  groupId: string;
  moduleId: string;
  isAssignment: boolean;
  id: string;
}) {
  const [sessionsData, setSessionsData] = useState<Awaited<ReturnType<typeof getSessions>>>();
  const [medialink, setMedialink] = useState<Medialink>();
  const [userMaterial, setUserMaterial] = useState<Awaited<ReturnType<typeof getUserMaterialById>>>();
  const { closeModal } = useContext(ModalContext);
  const handleError = useThrowAsyncError();
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();
  const { module, subModules } = useContext(ModuleContext);
  const { toc } = useTableOfContent();

  useEffect(() => {
    setIsLoading(true);

    Promise.all([
      getSessions({
        exerciseType: scorableItem.type,
        groupId,
        isAssignment,
        materialId: scorableItem.id,
        memberId,
        moduleId,
      }),
      scorableItem.type === 'medialink' ? getMedialinkById(moduleId, scorableItem.id) : undefined,
      scorableItem.type === 'user-material'
        ? getUserMaterialById(moduleId, scorableItem.id, { includeNodeHierarchy: true })
        : undefined,
    ])
      .then(([data, medialink, userMaterial]) => {
        setSessionsData(data);
        setMedialink(medialink);
        setUserMaterial(userMaterial);
      })
      .catch(error => {
        handleError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [scorableItem, groupId, isAssignment, memberId, moduleId, handleError]);

  function handleClose() {
    closeModal(id);
  }

  const materialModule = [module, ...subModules].find(m => m?.id === moduleId);

  const headerTags = useMemo(() => {
    if (scorableItem.type === 'medialink' && medialink) {
      return <MedialinkDialogHeaderTagsWithoutPillGroup material={medialink} module={materialModule!} />;
    }

    if (scorableItem.type === 'user-material' && userMaterial) {
      return (
        <UserMaterialTags
          material={userMaterial}
          module={materialModule!}
          isTeacherAlike={true}
          nodes={toc as unknown as Node[]}
        />
      );
    }

    return null;
  }, [scorableItem.type, medialink, userMaterial, materialModule, toc]);

  const containerRef = useRef(null);
  const tableRef = useRef(null);

  if (!module) return null;

  return (
    <Dialog
      id={id}
      modal
      className="history-modal"
      onClose={handleClose}
      size="max"
      aria-label={t(`${BASE_PATH}.DIALOG_LABEL`)}
    >
      <Dialog.Header
        title={t(`${BASE_PATH}.DIALOG_TITLE`, {
          memberName: sessionsData?.memberName,
          scorableItemName: scorableItem.name,
        })}
        onClose={handleClose}
        aria-label={t('ACTIONS.CLOSE')}
        meta={
          !isLoading ? (
            <PillGroup aria-label={undefined} role={undefined}>
              {headerTags}
              <Pill key="is-assignment">
                {isAssignment
                  ? t(`${BASE_PATH}.DIALOG_HEADER.ASSIGNMENT`)
                  : t(`${BASE_PATH}.DIALOG_HEADER.FREE_EXERCISE`)}
              </Pill>
              {!medialink?.assessmentQExercise?.isAdaptive && (
                <Pill key="amount-of-items">
                  {(medialink?.amountOfItems || 0) > 1
                    ? t(`${BASE_PATH}.DIALOG_HEADER.AMOUNT_OF_ITEMS.MULTIPLE`, {
                        amount: medialink?.amountOfItems,
                      })
                    : t(`${BASE_PATH}.DIALOG_HEADER.AMOUNT_OF_ITEMS.SINGLE`)}
                </Pill>
              )}
            </PillGroup>
          ) : undefined
        }
      />
      <Dialog.Body>
        <HorizontalScrollContextProvider containerRef={containerRef} tableRef={tableRef}>
          <div className="scroll-table-container" ref={containerRef}>
            {isLoading || !sessionsData || !module || !materialModule ? (
              <LoadingState variant="table" aria-label="loading" columns={5} rows={11} />
            ) : (
              <HistoryTable
                tableRef={tableRef}
                medialink={medialink}
                module={module}
                sessionsData={sessionsData}
                scorableItem={scorableItem}
                isAssignment={isAssignment}
                userMaterial={userMaterial}
                materialModule={materialModule}
                headerTags={
                  <PillGroup role={undefined} aria-label={undefined}>
                    {headerTags}
                  </PillGroup>
                }
              />
            )}
          </div>
        </HorizontalScrollContextProvider>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          'aria-label': t('ACTIONS.CLOSE'),
          role: 'group',
          children: (
            <Button prominence="neutral" onClick={handleClose}>
              {t('ACTIONS.CLOSE')}
            </Button>
          ),
        }}
      />
    </Dialog>
  );
}
